import { isNil } from "lodash";
import { type TenediCrownImportReportFieldsFragment } from "../../generated/graphql";

/** The backend refuses a larger export; checked here so the operator hears before uploading. */
export const MAX_EXPORT_ZIP_BYTES = 50 * 1024 * 1024;

/** The content type the upload URL is signed for. */
export const EXPORT_ZIP_CONTENT_TYPE = "application/zip";

/** Why `file` cannot be imported, or null when it can be uploaded. */
export const crownExportFileProblem = (file: {
  readonly name: string;
  readonly size: number;
}): string | null => {
  if (!/\.zip$/i.test(file.name)) {
    return "Choose the export's .zip file, as Crown's export script writes it.";
  }
  if (file.size === 0) {
    return "That file is empty.";
  }
  if (file.size > MAX_EXPORT_ZIP_BYTES) {
    return "The export is larger than 50 MB.";
  }
  return null;
};

export type CrownImportCounts = {
  readonly partners: number;
  readonly ready: number;
  readonly refused: number;
  readonly notImported: number;
};

export const crownImportCounts = (
  report: TenediCrownImportReportFieldsFragment,
): CrownImportCounts => {
  const refused = report.partners.filter(
    (partner) => !isNil(partner.error),
  ).length;
  return {
    partners: report.partners.length,
    ready: report.partners.length - refused,
    refused,
    notImported: report.notImportedPartners.length,
  };
};

const plural = (count: number, one: string, many: string): string =>
  `${count} ${count === 1 ? one : many}`;

/**
 * How the report names a partner's customer: the TMS customer it matched or
 * created first, since that is where its orders land, with the CFM customer
 * and account ID beneath. A customer still to be created goes by its CFM name.
 */
export const crownImportCustomerLabels = (
  partner: Pick<
    TenediCrownImportReportFieldsFragment["partners"][number],
    "customerName" | "accountId" | "tmsCustomerName"
  >,
): { readonly name: string; readonly source: string } =>
  isNil(partner.tmsCustomerName) ||
  partner.tmsCustomerName === partner.customerName
    ? { name: partner.customerName, source: partner.accountId }
    : {
        name: partner.tmsCustomerName,
        source: `CFM: ${partner.customerName}, ${partner.accountId}`,
      };

/**
 * The carrier identity the export names, for example
 * "Export SCAC XTIG · ISA IDs XTIG, XTIGA", so the operator can check the
 * export is this carrier's before applying.
 */
export const crownImportExportIdentity = (
  report: Pick<
    TenediCrownImportReportFieldsFragment,
    "carrierScac" | "carrierIsaIds"
  >,
): string => {
  const scac = report.carrierScac === "" ? "none" : report.carrierScac;
  const isaIds =
    report.carrierIsaIds.length === 0
      ? "none"
      : report.carrierIsaIds.join(", ");
  return `Export SCAC ${scac} · ${
    report.carrierIsaIds.length === 1 ? "ISA ID" : "ISA IDs"
  } ${isaIds}`;
};

/** The report's first line, for example "8 of 10 partners ready to import; 2 with a problem". */
export const crownImportSummary = (
  report: TenediCrownImportReportFieldsFragment,
): string => {
  const counts = crownImportCounts(report);
  const parts = [
    `${counts.ready} of ${plural(counts.partners, "partner", "partners")} ${
      report.applied ? "imported" : "ready to import"
    }`,
  ];
  if (counts.refused > 0) {
    parts.push(`${counts.refused} with a problem`);
  }
  if (counts.notImported > 0) {
    parts.push(`${counts.notImported} left for setting up by hand`);
  }
  return parts.join("; ");
};
